import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as actions from '../actions/index';
import { catchError, exhaustMap, map, of, switchMap } from 'rxjs';
import {
    PermPostOfferLinks,
    PermPostOfferService
} from 'src/app/accountmanagers/perm-post-offer/services/perm-post-offer.service';
import { PermPostOfferWorkflowModalManagerService } from 'src/app/accountmanagers/perm-post-offer/services/perm-post-offer-workflow-modal-manager.service';
import { PermanentCandidateItemModel } from 'src/app/shared/models/permanent-candidate-item.model';
import { AccountManagersService } from 'src/app/accountmanagers/accountmanagers.service';
import { PermPostOfferOfferModel } from '../models/perm-post-offer-offer.model';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { PermanentJobOfferDashboardStatusEnum } from '../../../accountmanagers/perm-post-offer/enums/permanent-job-offer-dashboard-status.enum';
import { PermPostOfferConfirmNotStartingModalPayload } from '../models/perm-post-offer-confirm-not-starting.model';
import { PermPostOfferConfirmNotStartingModalComponent } from 'src/app/accountmanagers/submittals/am-submittal-status-update/perm-post-offer-confirm-not-starting-modal/perm-post-offer-confirm-not-starting-modal.component';
import {
    openPermPostOfferConfirmNotStartingModalFromGrid,
    setJobOfferStatusToOfferAcceptedNotStartingConfirmed,
    setJobOfferStatusToOfferAcceptedNotStartingConfirmedFailure,
    setJobOfferStatusToOfferAcceptedNotStartingConfirmedSuccess
} from '../actions';

export class PermPostOfferConfirmNotStartingModalEffectsToasterMessages {
    static readonly OfferAcceptedNotStartingConfirmedSuccessMessage =
        'The candidate has been removed from the Pre Start dashboard successfully.';

    static readonly OfferAcceptedNotStartingConfirmedFailureMessage =
        'Something went wrong while removing the candidate, please try again.';
}

@Injectable()
export class PermPostOfferConfirmNotStartingModalEffects {
    openPermPermPostOfferConfirmNotStartingModalFromGrid$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(openPermPostOfferConfirmNotStartingModalFromGrid),
                exhaustMap((action) =>
                    this._accountManagerService
                        .getJobTitle(action.model.jobId)
                        .pipe(
                            map((jobTitle) => this.createPermPostOfferConfirmNotStartingModal(action.model, jobTitle))
                        )
                )
            );
        },
        { dispatch: false }
    );

    setJobOfferStatusToOfferAcceptedNotStartingConfirmed$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(setJobOfferStatusToOfferAcceptedNotStartingConfirmed),
            switchMap((action) =>
                this._permPostOfferService
                    .updatePermanentJobOfferStatus(
                        action.jobOfferId,
                        PermanentJobOfferDashboardStatusEnum.OfferAcceptedNotStartingConfirmed
                    )
                    .pipe(
                        map(() => {
                            return setJobOfferStatusToOfferAcceptedNotStartingConfirmedSuccess();
                        }),
                        catchError((error: unknown) => {
                            return of(setJobOfferStatusToOfferAcceptedNotStartingConfirmedFailure({ error }));
                        })
                    )
            )
        );
    });

    setJobOfferStatusToOfferAcceptedNotStartingConfirmedFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(setJobOfferStatusToOfferAcceptedNotStartingConfirmedFailure),
            map(() =>
                showToasterMessage({
                    message:
                        PermPostOfferConfirmNotStartingModalEffectsToasterMessages.OfferAcceptedNotStartingConfirmedFailureMessage
                })
            )
        );
    });

    setJobOfferStatusToOfferAcceptedNotStartingConfirmedSuccess$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(setJobOfferStatusToOfferAcceptedNotStartingConfirmedSuccess),
            map(() => {
                this._permPostOfferWorkflowModalManagerService.closeModal();
                return showToasterMessage({
                    message:
                        PermPostOfferConfirmNotStartingModalEffectsToasterMessages.OfferAcceptedNotStartingConfirmedSuccessMessage
                });
            })
        );
    });

    cancelPermPostOfferConfirmNotStartingModal$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(actions.cancelPermPostOfferConfirmNotStartingModal),
                map(() => this._permPostOfferWorkflowModalManagerService.closeModal())
            );
        },
        { dispatch: false }
    );

    constructor(
        private readonly _actions$: Actions,
        private readonly _permPostOfferService: PermPostOfferService,
        private readonly _permPostOfferWorkflowModalManagerService: PermPostOfferWorkflowModalManagerService,
        private readonly _accountManagerService: AccountManagersService
    ) {}

    private createPermPostOfferConfirmNotStartingModal(
        actionPayload: PermanentCandidateItemModel,
        jobTitle: string
    ): void {
        const data = this.generatePermPostOfferConfirmNotStartingModalPayload(jobTitle, actionPayload);
        this._permPostOfferWorkflowModalManagerService.createModal(
            PermPostOfferConfirmNotStartingModalComponent,
            data,
            '700px',
            '700px'
        );
    }

    private generatePermPostOfferConfirmNotStartingModalPayload(
        jobTitle: string,
        payload: PermanentCandidateItemModel
    ): PermPostOfferConfirmNotStartingModalPayload {
        const permPostOfferOfferModel: PermPostOfferOfferModel = {
            facilityName: payload.facilityName,
            facilityLink: PermPostOfferLinks.FacilityLink(payload.facilityId),
            candidateName: `${payload.candidateFirstName} ${payload.candidateLastName}`,
            candidateLink: PermPostOfferLinks.CandidateLinkByContractorStatus(
                payload.isSubcontractor,
                payload.candidateId
            ),
            jobName: jobTitle,
            jobLink: PermPostOfferLinks.JobLink(payload.jobId),
            displayOfferStatusSelection: false
        };
        return {
            permanentJobOfferId: payload.permanentJobOfferId,
            permPostOfferOfferModel
        };
    }
}
